<template>
  <div class="elementsText">
    <section class="notSummary">
      <section
        v-if="errorTextsForElements[propsObj.id] != '' && getIsEditable"
        :id="'errorTextElement' + propsObj.id"
        :style="{ visibility: showIfNotTranslation }"
        class="errorMessage"
      >
        {{ errorTextsForElements[propsObj.id] }}
      </section>

      <section v-if="getIsEditable" key="secEditable" class="wrapper">
        <section v-if="!showEditorBool" class="question-text text ProseMirror">
          <span
            :id="'textEditorClosed' + propsObj.id + (isTranslation ? 'Translation' : '')"
            v-html="sanitizeHTML(content)"
            class="unfocusedTextElement"
            :class="[isTranslationMode ? 'textElementDisplay' : 'textElementDisplayFullScreen']"
            @click="showEditor"
          />
        </section>
        <div v-show="showEditorBool" :key="reInitKey">
          <TextEditor
            :id="'textEditor' + propsObj.id + (isTranslation ? 'Translation' : '')"
            :propsObj="propsObj"
            :isTranslation="isTranslation"
            :studyId="studyId"
            v-model="content"
            @showMediaReferenceModal="showMediaReferenceModal"
            @input="
              $emit(
                'change-element',
                {
                  type: 'translation-attribute',
                  name: 'text',
                  isTranslation: isTranslation,
                  value: replaceLabelsWithPositions(content),
                },
              )
            "
          ></TextEditor>
        </div>
      </section>
      <section
        v-else
        :id="'textEditorNotEditable' + propsObj.id + (isTranslation ? 'Translation' : '')"
        key="secNotEditable"
        class="wrapper"
        style="margin-bottom: 10px;"
      >
        <p class="question-text text nonEditableEditorField ProseMirror">
          <span v-html="sanitizeHTML(content)"></span>
        </p>
      </section>

      <section v-if="getIsEditable" class="hide">
        {{ content }}
      </section>

      <section v-if="!isTranslation" class="wrapper">
        <label>
          {{ $t('elementsTextTranslation.textTypeDesc') }} *
        </label>
        <select
          :value="propsObj.type"
          :disabled="disableInputs"
          :id="'inputTextType' + propsObj.id"
          class="textTypeSelect"
          @input="$emit('change-element', { type: 'attribute', name: 'type', value: $event.target.value })"
        >
          <option
            value=""
            :id="'inputTextType' + propsObj.id + 'None'"
            key="empty"
            disabled
          >
            {{ textTypeEmpty }}
          </option>
          <option
            v-for="data in textTypeList"
            :value="data"
            :id="'inputTextType' + propsObj.id + data"
            :key="data"
          >
            {{ $t('elementsTextTranslation.' + data) }}
          </option>
        </select>
      </section>
    </section>
  </div>
</template>

<script>
import Vue from "vue";
import { mapGetters } from "vuex";
import util from "../mixins/utils";
import TextEditor from "./TextEditor.vue";

export default {
  name: "ElementsText",

  components: {
    TextEditor,
  },

  mixins: [util],

  props: {
    propsObj: {
      required: true,
      type: Object,
    },

    translation: {
      required: true,
      type: String,
    },

    isTranslation: {
      required: true,
      type: Boolean,
    },

    errorTextsForElements: {
      required: true,
      type: Array,
    },

    isTranslationMode: {
      required: true,
      type: Boolean,
    },

    showTextEditor: {
      required: true,
      type: Number,
    },

    studyId: {
      required: true,
      type: Number,
    },

    isSkill: {
      required: true,
      type: Boolean,
    },

    allowStructureChanges: {
      type: Boolean,
      default: null,
    },
  },

  data: function() {
    return {
      showEditorBool: false,
      reInitKey: 0,
    };
  },

  computed: {
    ...mapGetters([
      "getIsEditable",
      "getQuestionLabelList",
    ]),

    textTypeList: function() {
      return ["none", "important", "tip", "info", "success", "highlight"];
    },

    showIfNotTranslation: function() {
      return !this.isTranslation ? "visible" : "hidden";
    },

    textTypeEmpty: function() {
      if (this.getIsEditable) {
        return Vue.i18n.translate("elementsTextTranslation.textType");
      } else {
        return Vue.i18n.translate("elementsTextTranslation.none");
      }
    },

    disableInputs: function() {
      return !this.getIsEditable || this.allowStructureChanges === false;
    },
  },

  watch: {
    showTextEditor: function(newVal) {
      if (newVal != -1) {
        this.showEditorBool = newVal == this.propsObj.id;
      }
    },

    //needs deep:true & handler because array in array changes -> 'normal' watch function does not detect changes in nested array
    getQuestionLabelList: {
      handler: function() {
        this.content = this.replacePositionsWithLabels(this.translation);
        this.reInitKey++;
      },
      deep: true,
    },

    translation: function(newVal) {
      if (newVal != "") {
        if (!newVal.includes("cursor: nwse-resize")) {
          this.content = this.replacePositionsWithLabels(newVal);
        }
      } else {
        this.content = "";
      }
    },
  },

  created() {
    if (this.showTextEditor != -1 && this.showTextEditor === this.propsObj.id) {
      this.showEditorBool = true;
    }
    this.content = this.replacePositionsWithLabels(this.translation);
  },

  methods: {
    replacePositionsWithLabels: function(textProp) {
      if (!textProp) {
        return "";
      }
      //first replace gets {{number}} as x -> brackets are replaced in second replace -> only number -> get label
      return textProp.replace(/\{{2}([^}{]+)}{2}/g, (x) => {
        const questionId = x.slice(2, x.length - 2);
        if (questionId === "" || this.getQuestionLabelList[questionId] == 0) {
          return "";
        }
        return `{{${this.getQuestionLabelList[questionId].label}}}`;
      });
    },

    replaceLabelsWithPositions: function(textProp) {
      const text = textProp.replace(/"/g, "'");
      //first replace gets {{label}} as x -> brackets are replaced in second replace -> only label -> get index
      //if label not in questionlabellist -> return ""
      return text.replace(/\{{2}([^}{]+)}{2}/g, (x) => {
        const label = x.slice(2, x.length - 2);
        const questionId = this.getQuestionLabelList.findIndex(
          (elem) => elem && elem.label === label,
        );
        if (questionId === -1) {
          return "";
        }
        return `{{${questionId}}}`;
      });
    },

    showMediaReferenceModal: function(show) {
      this.$emit("change-element", { type: "show-media-reference-modal", value: show });
    },

    showEditor: function() {
      this.showEditorBool = true;
      this.$emit("change-element", { type: "show-texteditor", value: this.propsObj.id });
    },
  },
}
</script>

<style scoped>
.nonEditableEditorField {
  width: var(--text-element-width);
  font-size: 16px;
}
</style>
